/**
 * Navbar global omnisearch (v0.34.0, WS-C).
 *
 * Debounced 300 ms against ``GET /api/search``; renders the grouped
 * hits (chassis / ONUs / MACs) in a dropdown. Keyboard-first:
 * ``/`` focuses the box from anywhere (ignored while typing in an
 * input), arrows move the highlight, Enter navigates, Esc closes.
 *
 * Navigation targets:
 * - chassis → ``/chassis/{id}``
 * - onu     → ``/chassis/{id}#tab=inventory``
 * - mac     → ``/tools/macs?search={mac}``
 */
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "../../api/client";

type SearchItem = {
  chassis_id: number | null;
  label: string;
  sublabel: string | null;
  port: string | null;
  display_id: string | null;
  serial: string | null;
  mac: string | null;
};

type SearchGroup = { kind: string; items: SearchItem[] };

type SearchResponse = { groups: SearchGroup[] };

type FlatHit = { kind: string; item: SearchItem };

const GROUP_LABELS: Record<string, string> = {
  chassis: "Chassis",
  onu: "ONUs",
  mac: "MACs",
};

/** True when the keydown target is already a typing surface — the
 *  ``/`` hotkey must not steal keystrokes from other inputs. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
}

function hitTarget(hit: FlatHit): string {
  if (hit.kind === "mac" && hit.item.mac) {
    return `/tools/macs?search=${encodeURIComponent(hit.item.mac)}`;
  }
  if (hit.kind === "onu" && hit.item.chassis_id != null) {
    return `/chassis/${hit.item.chassis_id}#tab=inventory`;
  }
  return `/chassis/${hit.item.chassis_id}`;
}

export function GlobalSearch() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  // 300 ms debounce — one query per typing pause, not per keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 300);
    return () => window.clearTimeout(t);
  }, [q]);

  const search = useQuery<SearchResponse>({
    queryKey: ["v2", "global-search", debounced],
    queryFn: async () => {
      const res = await api.get<SearchResponse>(
        `/search?q=${encodeURIComponent(debounced)}`,
      );
      return res.data;
    },
    enabled: debounced.length >= 2,
    staleTime: 10_000,
    retry: 0,
  });

  const groups = useMemo(
    () => (debounced.length >= 2 ? search.data?.groups ?? [] : []),
    [debounced, search.data],
  );
  // Flattened hit list drives the arrow-key highlight across group
  // boundaries; the dropdown renders per-group with a running index.
  const hits = useMemo<FlatHit[]>(
    () => groups.flatMap((g) => g.items.map((item) => ({ kind: g.kind, item }))),
    [groups],
  );

  useEffect(() => setActive(0), [hits]);

  // Click outside closes the dropdown.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  // Global "/" hotkey focuses the box from anywhere in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = useCallback(
    (hit: FlatHit) => {
      setOpen(false);
      setQ("");
      inputRef.current?.blur();
      navigate(hitTarget(hit));
    },
    [navigate],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!open || hits.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % hits.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + hits.length) % hits.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const hit = hits[active];
      if (hit) go(hit);
    }
  };

  const showEmpty =
    open &&
    debounced.length >= 2 &&
    !search.isFetching &&
    hits.length === 0;

  return (
    <div ref={containerRef} className="relative w-full max-w-sm">
      <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-slate-500" />
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-expanded={open && hits.length > 0}
        aria-label="Global search"
        placeholder="Search chassis, serial, MAC…  ( / )"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="w-full rounded border border-slate-700 bg-slate-950 py-1.5 pl-7 pr-6 text-sm text-slate-100 placeholder:text-slate-500 focus-visible:border-emerald-700 focus-visible:outline-none"
      />
      {search.isFetching && (
        <Loader2 className="pointer-events-none absolute right-2 top-2 h-3.5 w-3.5 animate-spin text-slate-500" />
      )}
      {open && (hits.length > 0 || showEmpty) && (
        <div
          className={[
            "absolute left-0 right-0 top-full z-50 mt-1.5 max-h-96",
            "overflow-y-auto rounded-lg border p-1 shadow-lg",
            "border-slate-700 bg-slate-900 text-slate-200",
          ].join(" ")}
        >
          {showEmpty ? (
            <p className="px-2 py-1.5 text-xs text-slate-500">
              No matches for “{debounced}”.
            </p>
          ) : (
            (() => {
              // Running index keeps the flat highlight aligned with
              // the grouped rendering.
              let idx = -1;
              return groups.map((g) => (
                <div key={g.kind}>
                  <div className="px-2 pb-0.5 pt-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                    {GROUP_LABELS[g.kind] ?? g.kind}
                  </div>
                  {g.items.map((item) => {
                    idx += 1;
                    const i = idx;
                    const isActive = i === active;
                    return (
                      <button
                        key={`${g.kind}-${i}`}
                        type="button"
                        onMouseEnter={() => setActive(i)}
                        onClick={() => go({ kind: g.kind, item })}
                        className={[
                          "block w-full cursor-pointer rounded px-2 py-1.5",
                          "text-left text-sm outline-none",
                          isActive ? "bg-slate-800 text-white" : "",
                        ].join(" ")}
                      >
                        <div
                          className={
                            g.kind === "mac" ? "font-mono text-xs" : "font-medium"
                          }
                        >
                          {item.label}
                        </div>
                        {item.sublabel && (
                          <div className="mt-0.5 text-[11px] text-slate-500">
                            {item.sublabel}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ));
            })()
          )}
        </div>
      )}
    </div>
  );
}
